/*
*   This content is licensed according to the W3C Software License at
*   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
*/

/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */

import { forEach } from 'lodash';
import MenubarItem from './menubar-item-links';

/* == IMPORTS =============================================================== */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */

const
    moduleName = 'menubar-links',
    baseCssClass = `js-${moduleName}`,

    selectors = {
        base: `.${baseCssClass}`
    };

/* == PRIVATE VARIABLES ===================================================== */



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

export default class Menubar {

    constructor(baseElement) {
        const msgPrefix = 'Menubar constructor argument menubarNode ';

        // Check whether menubarNode is a DOM element
        if (!baseElement instanceof Element) {
            throw new TypeError(msgPrefix + 'is not a DOM Element.');
        }

        // Check whether menubarNode has descendant elements
        if (baseElement.childElementCount === 0) {
            throw new Error(msgPrefix + 'has no element children.');
        }

        // Check whether menubarNode has A elements
        let e = baseElement.firstElementChild;
        while (e) {
            const menubarItem = e.firstElementChild;
            if (e && menubarItem && menubarItem.tagName !== 'A') {
                throw new Error(msgPrefix + 'has child elements are not A elements.');
            }
            e = e.nextElementSibling;
        }

        this.isMenubar = true;

        this.baseEl = baseElement;

        this.menubarItems = []; // See Menubar init method
        this.firstChars = []; // See Menubar init method

        this.firstItem = null; // See Menubar init method
        this.lastItem = null; // See Menubar init method

        this.hasFocus = false; // See MenubarItem handleFocus, handleBlur
        this.hasHover = false; // See Menubar handleMouseover, handleMouseout

        this.init();
    }

    setFocusToItem(newItem) {
        let flag = false;

        forEach(this.menubarItems, (menuBaritem) => {
            if (menuBaritem.baseElement.tabIndex == 0) {
                flag = menuBaritem.baseElement.getAttribute('aria-expanded') === 'true';
            }

            menuBaritem.baseElement.tabIndex = -1;
            if (menuBaritem.popupMenu) {
                menuBaritem.popupMenu.Close();
            }
        })
        
        newItem.baseElement.focus();
        newItem.baseElement.tabIndex = 0;

        if (flag && newItem.popupMenu) {
            newItem.popupMenu.Open();
        }
    }

    setFocusToFirstItem() {
        this.setFocusToItem(this.firstItem);
    }

    setFocusToLastItem() {
        this.setFocusToItem(this.lastItem);
    }

    setFocusToPreviousItem(currentItem) {
        let newItem;

        if (currentItem === this.firstItem) {
            newItem = this.lastItem;
        } else {
            newItem = this.menubarItems[this.menubarItems.indexOf(currentItem) - 1];
        }

        this.setFocusToItem(newItem);
    }

    setFocusToNextItem(currentItem) {
        let newItem;

        if (currentItem === this.lastItem) {
            newItem = this.firstItem;
        } else {
            newItem = this.menubarItems[this.menubarItems.indexOf(currentItem) + 1];
        }

        this.setFocusToItem(newItem);
    }

    setFocusByFirstCharacter(currentItem, char) {
        let start, index;

        char = char.toLowerCase();

        // Get start index for search based on position of currentItem
        start = this.menubarItems.indexOf(currentItem) + 1;
        if (start === this.menubarItems.length) {
            start = 0;
        }

        // Check remaining slots in the menu
        index = this.getIndexFirstChars(start, char);

        // If not found in remaining slots, check from beginning
        if (index === -1) {
            index = this.getIndexFirstChars(0, char);
        }

        // If match was found...
        if (index > -1) {
            this.setFocusToItem(this.menubarItems[index]);
        }
    };

    getIndexFirstChars(startIndex, char) {
        for (var i = startIndex; i < this.firstChars.length; i++) {
            if (char === this.firstChars[i]) {
                return i;
            }
        }
        return -1;
    };

    /**
     * @method init
     * 
     * @description
     *      Adds ARIA role to the menubar node
    *       Traverse menubar children for A elements to configure each A element as a ARIA menuitem
    *       and populate menuitems array. Initialize firstItem and lastItem properties.
     **/
    init() {
        let menubarItem, menuElement, textContent, numItems;

        // Traverse the element children of menubarNode: configure each with
        // menuitem role behavior and store reference in menuitems array.
        let elem = this.baseEl.firstElementChild;

        while (elem) {
            menuElement = elem.firstElementChild;

            if (elem && menuElement && menuElement.tagName === 'A') {
                menubarItem = new MenubarItem(menuElement, this);
                menubarItem.init();
                this.menubarItems.push(menubarItem);
                textContent = menuElement.textContent.trim();
                this.firstChars.push(textContent.substring(0, 1).toLowerCase());
            }

            elem = elem.nextElementSibling;
        }

        // Use populated menuitems array to initialize firstItem and lastItem.
        numItems = this.menubarItems.length;
        if (numItems > 0) {
            this.firstItem = this.menubarItems[0];
            this.lastItem = this.menubarItems[numItems - 1];
        }
        this.firstItem.baseElement.tabIndex = 0;
    }
}

/* == PRIVATE METHODS ======================================================= */



/* ========================================================================== *\
	INITIALIZATION
\* ========================================================================== */

const initMenubar = () => {
    forEach(document.querySelectorAll(selectors.base), cardElement => {
        const menubar = new Menubar(cardElement);
    });
}

initMenubar();
/* == INITIALIZATION ======================================================== */